import { useNavigate } from "react-router-dom";
import { Button, Container, Eyebrow } from "@constructorfleet/ui/design-system";
import { useOfferings } from "../offerings/use-offerings";
import { useAuth } from "../context/auth-context";
import { ROUTES } from "../constants";
import { formatOfferingPricing } from "../offerings/format-pricing";

export function PricingPage() {
  const { offerings } = useOfferings();
  const navigate = useNavigate();
  let auth: { username?: string; role?: string; email?: string } | null = null;
  try {
    const authCtx = useAuth();
    auth = authCtx?.auth ?? null;
  } catch {
    auth = null;
  }

  const goToSignup = (signupPath: string) => {
    if (auth) {
      navigate(`/${signupPath}`);
    } else {
      navigate(ROUTES.LOGIN, { state: { from: `/${signupPath}` } });
    }
  };

  return (
    <div className="pricing-page">
      {/* ── Header ──────────────────────────────────────────────── */}
      <section className="ph">
        <Container>
          <Eyebrow brand dot>
            Pricing
          </Eyebrow>
          <h1>Start free for 30 days.</h1>
          <p>
            No hidden fees. No long-term contracts. Cancel anytime — and if you cancel before day
            30, you&apos;re never charged.
          </p>
        </Container>
      </section>

      {/* ── Plans ───────────────────────────────────────────────── */}
      <section className="plans">
        <Container>
          <div className="plan-grid">
            {offerings.map((offering) => {
              const price = formatOfferingPricing(
                offering.billing?.pricing ?? offering.manifest?.pricing
              );
              const marketing = offering.marketing as Record<string, unknown> | undefined;
              const features = (marketing?.highlights as string[]) ?? [];
              const trialDays =
                offering.billing?.pricing?.trialDays ?? offering.manifest?.pricing?.trialDays ?? 0;

              return (
                <div className="plan" key={offering.id}>
                  <span className="tag">{offering.name}</span>
                  <h2>{offering.marketing?.summary ?? offering.name}</h2>
                  {marketing?.tagline ? <p className="sub">{marketing.tagline as string}</p> : null}
                  {price && (
                    <div className="price">
                      <span className="num">{price}</span>
                      {trialDays > 0 && (
                        <span className="sub"> after your {trialDays}-day free trial</span>
                      )}
                    </div>
                  )}
                  <div className="fineprint">
                    {trialDays > 0
                      ? `Free for ${trialDays} days, then flat rate. No setup fees. Cancel anytime.`
                      : "Flat rate. No setup fees. Cancel anytime."}
                  </div>
                  {features.length > 0 ? (
                    <ul>
                      {features.map((feature: string, i: number) => (
                        <li key={i}>{feature}</li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="btn-row">
                    {offering.routes.signupPath && (
                      <Button
                        size="lg"
                        variant="brand"
                        onClick={() => goToSignup(offering.routes.signupPath!)}
                        style={{ flex: 1 }}
                      >
                        {offering.marketing?.primaryCtaLabel ?? "Get started"}
                      </Button>
                    )}
                    <Button
                      as="a"
                      href={`/${offering.routes.productPath}`}
                      size="lg"
                      variant="secondary"
                    >
                      Learn more
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── Add-ons ─────────────────────────────────────────────── */}
      {offerings.some((o) => (o.addons ?? []).length > 0) ? (
        <section className="addons">
          <Container>
            <div className="addons-head">
              <h2>Optional add-ons</h2>
              <p>Available after signup.</p>
            </div>
            <div className="addons-row">
              {offerings.flatMap((offering) =>
                (offering.addons ?? []).map((addon) => (
                  <div className="addon" key={addon.id}>
                    <div className="ic">
                      <svg viewBox="0 0 24 24">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                      </svg>
                    </div>
                    <div className="meta">
                      <b>{addon.name}</b>
                      {addon.marketing?.summary ? <span>{addon.marketing.summary}</span> : null}
                    </div>
                    <div className="price">
                      <b>{formatOfferingPricing(addon.pricing)}</b>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section className="faq">
        <Container>
          <div className="faq-inner">
            <h2>Questions we get asked a lot</h2>
            <div className="qa">
              <b>How does the free trial work?</b>
              <p>
                Your first 30 days are free. We collect a card at signup so service continues
                without a gap, but nothing is charged until day 30. Cancel any time before then and
                you pay nothing. The trial is available once per member.
              </p>
            </div>
            <div className="qa">
              <b>Do I get anything for referring someone?</b>
              <p>
                Yes. Once someone you referred starts a paid month, you get $1.50 off your own bill
                every month they stay — plus $0.75 a month for anyone they refer. Credit is applied
                to your next invoice automatically; ten direct referrals covers your membership
                entirely. Your referral link is in your dashboard.
              </p>
            </div>
            <div className="qa">
              <b>Is there a contract or commitment?</b>
              <p>
                No. You can cancel or change your plan at any time from your account settings. You
                only pay for the months you use.
              </p>
            </div>
            <div className="qa">
              <b>What if the call fails or the line is down?</b>
              <p>
                We notify you — every attempt is logged, whether it succeeds or not. If
                something&apos;s wrong, you&apos;ll know in time to call in yourself.
              </p>
            </div>
            <div className="qa">
              <b>Can I keep my call recordings longer than 30 days?</b>
              <p>
                Yes — the Extended Audio Storage add-on keeps your recordings for up to one year.
                Transcripts are retained indefinitely at no extra charge.
              </p>
            </div>
            <div className="qa">
              <b>Do you accept insurance or HSA/FSA cards?</b>
              <p>
                ConstructorFleet is not a healthcare service and is not billed through insurance. We
                accept major credit and debit cards, including HSA/FSA cards that allow
                general-purpose charges.
              </p>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
